import type { ReactNode } from "react";
import { regionOptions, typeOptions, yearOptions } from "@/mocks/dramas";

type Props = {
  selectedTypes: string[];
  selectedYears: number[];
  selectedRegions: string[];
  onToggleType: (value: string) => void;
  onToggleYear: (value: number) => void;
  onToggleRegion: (value: string) => void;
  onReset: () => void;
};

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 font-label text-xs transition-colors ${
        active
          ? "border-primary-500 bg-primary-500 text-background-50"
          : "border-background-300/60 bg-background-100 text-foreground-700 hover:border-primary-400 hover:text-foreground-950"
      }`}
    >
      {children}
    </button>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-background-200/60 py-4 last:border-b-0 last:pb-0 first:pt-0">
      <p className="font-label text-[11px] uppercase tracking-[0.24em] text-accent-600">{label}</p>
      <div className="mt-2.5 flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({
  selectedTypes,
  selectedYears,
  selectedRegions,
  onToggleType,
  onToggleYear,
  onToggleRegion,
  onReset,
}: Props) {
  const activeCount =
    selectedTypes.length + selectedYears.length + selectedRegions.length;

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100/80 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-label text-sm font-medium text-foreground-900">
          <span className="flex h-4 w-4 items-center justify-center text-primary-500">
            <i className="ri-filter-3-line text-sm"></i>
          </span>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[11px] text-primary-500">
              已选 {activeCount}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
            activeCount === 0
              ? "cursor-not-allowed border-background-300/40 text-foreground-500/60"
              : "cursor-pointer border-background-300/60 text-foreground-700 hover:border-primary-400 hover:text-primary-500"
          }`}
        >
          <i className="ri-refresh-line text-xs"></i>
          重置
        </button>
      </div>

      <div className="mt-4">
        <Group label="类型">
          {typeOptions.map((type) => (
            <Chip key={type} active={selectedTypes.includes(type)} onClick={() => onToggleType(type)}>
              {type}
            </Chip>
          ))}
        </Group>
        <Group label="年份">
          {yearOptions.map((year) => (
            <Chip key={year} active={selectedYears.includes(year)} onClick={() => onToggleYear(year)}>
              {year} 年
            </Chip>
          ))}
        </Group>
        <Group label="地区">
          {regionOptions.map((region) => (
            <Chip
              key={region}
              active={selectedRegions.includes(region)}
              onClick={() => onToggleRegion(region)}
            >
              {region}
            </Chip>
          ))}
        </Group>
      </div>
    </div>
  );
}